import React from 'react';
import { Divider, List, ListItem, ListItemButton, ListItemIcon, ListItemText } from "@mui/material";
import { IIRSA_ITEMS } from "../Navbar/constants";
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined';
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined';
import FormatListBulletedOutlinedIcon from '@mui/icons-material/FormatListBulletedOutlined';
import SpaceDashboardOutlinedIcon from '@mui/icons-material/SpaceDashboardOutlined';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import style from './styles.module.scss';
import { useNavigate, useLocation } from "react-router-dom";
import { Drawer as MuiDrawer } from "./styled";
import Tooltip from '@mui/material/Tooltip';
import i18next from 'i18next';
import { activeMenuPath } from './activeItem';
import { useCachedResource } from 'app/hooks/useCachedResource';
import { getStudies, Study } from 'config/services/studies.service';
import './i18n';

const studiesCache = new Map<string, Study[]>();
const STUDIES_LIST_ID = 'drawer-studies';

interface Props {
    open: boolean;
    closeDrawer: () => void;
    openDrawer: () => void;
}

const Drawer = ({ open, closeDrawer, openDrawer }: Props) => {
    const navigate = useNavigate();
    const location = useLocation();
    // Titles come in the current language, which only changes with a reload.
    const studies = useCachedResource(studiesCache, i18next.language || 'es', getStudies, []) || [];
    const [studiesExpanded, setStudiesExpanded] = React.useState(false);

    // "Estudios" is not a page: with the menu closed it opens the menu already expanded.
    const toggleStudies = () => {
        if (!open) {
            openDrawer();
            setStudiesExpanded(true);
        } else {
            setStudiesExpanded(!studiesExpanded);
        }
    };

    React.useEffect(() => {
        if (!open) {
            setStudiesExpanded(false);
        }
    }, [open]);

    const getIcon = (iconName: string | undefined, isActive: boolean) => {
        const fillcolor = isActive ? '#384078' : 'white';
        switch (iconName) {
            case 'home':
                return <HomeOutlinedIcon style={{color: fillcolor}}/>;
            case 'map':
                return <PublicOutlinedIcon style={{color: fillcolor}}/>;
            case 'back':
                return <ArrowBackIcon style={{color: fillcolor}}/>;
            case 'forward':
                return <ArrowForwardIcon style={{color: fillcolor}}/>;
            case 'road':
                return <SpaceDashboardOutlinedIcon style={{color: fillcolor}}/>;
            case 'projects':
                return <FormatListBulletedOutlinedIcon style={{color: fillcolor}}/>;
            case 'charts':
                return <BarChartOutlinedIcon style={{color: fillcolor}}/>;
            default:
                return <></>;
        }
    }

    // The open menu covers the page: it closes after choosing a page, or with Escape.
    const navigateTo = (path: string) => {
        navigate(path);
        closeDrawer();
    }

    React.useEffect(() => {
        if (!open) {
            return undefined;
        }
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                closeDrawer();
            }
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [open, closeDrawer]);

    const activePath = activeMenuPath(location.pathname, IIRSA_ITEMS.map(item => item.pathname));

    const switchOpenSate = () => {
        if (open) {
            closeDrawer();
        }
        else {
            openDrawer();
        }
    }


    return (
        <MuiDrawer variant="permanent" open={open}
            classes={{
                paper: style.drawer
            }}>
            <div style={{ height: '90px' }}></div>
            <List classes={{
                root: style.drawerList

            }}>
                {IIRSA_ITEMS.map((item, index) => {
                    const isActive = item.pathname === activePath;
                    return (
                        <ListItem key={item.pathname} onClick={() => navigateTo(item.pathname)}>
                            <Tooltip title={item.name}>
                                <ListItemButton
                                    aria-label={String(item.name)}
                                    sx={[
                                        {
                                            minHeight: 48,
                                            px: 2.5,
                                        },
                                        open
                                            ? {
                                                justifyContent: 'initial',
                                            }
                                            : {
                                                justifyContent: 'center',
                                            },
                                    ]}>
                                    <ListItemIcon
                                        className={style.drawerListIcon}
                                        sx={[
                                            {
                                                minWidth: 0,
                                                justifyContent: 'center',
                                                padding: '8px',
                                                borderRadius: '50%',
                                                transition: 'all 0.3s ease',
                                            },
                                            open
                                                ? {
                                                    mr: 2,
                                                }
                                                : {
                                                    mr: 'auto',
                                                },
                                            isActive && {
                                                backgroundColor: '#FFF'
                                            },
                                        ]}>
                                        {getIcon(item.iconName, isActive)}
                                    </ListItemIcon>
                                <ListItemText primary={item.name} className={style.drawerListItem} hidden={!open} />
                            </ListItemButton>
                        </Tooltip>
                    </ListItem>
                    );
                })}
                {studies.length > 0 && (
                    <ListItem className={studiesExpanded ? style.studiesExpanded : undefined}>
                        <Tooltip title={i18next.t('drawer:studies')}>
                            <ListItemButton
                                aria-label={String(i18next.t('drawer:studies'))}
                                aria-expanded={open && studiesExpanded}
                                aria-controls={STUDIES_LIST_ID}
                                onClick={toggleStudies}
                                sx={[
                                    { minHeight: 48, px: 2.5 },
                                    open ? { justifyContent: 'initial' } : { justifyContent: 'center' },
                                ]}>
                                <ListItemIcon
                                    className={style.drawerListIcon}
                                    sx={[
                                        { minWidth: 0, justifyContent: 'center', padding: '8px' },
                                        open ? { mr: 2 } : { mr: 'auto' },
                                    ]}>
                                    <MenuBookOutlinedIcon style={{color: 'white'}}/>
                                </ListItemIcon>
                                <ListItemText primary={i18next.t('drawer:studies')} className={style.drawerListItem}
                                              hidden={!open} />
                                {open && <ExpandMoreIcon
                                    className={`${style.chevron} ${studiesExpanded ? style.chevronUp : ''}`} />}
                            </ListItemButton>
                        </Tooltip>
                    </ListItem>
                )}
                {open && studiesExpanded && (
                    <li id={STUDIES_LIST_ID} className={style.studies}>
                        <ul>
                            {studies.map(study => (
                                <li key={study.id}>
                                    <a href={study.url} target="_blank" rel="noopener noreferrer"
                                       className={style.study} onClick={closeDrawer}
                                       aria-label={`${study.title} (${i18next.t('drawer:opensInNewTab')})`}>
                                        <span>{study.title}</span>
                                        <OpenInNewIcon className={style.studyIcon} aria-hidden="true" />
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </li>
                )}
            </List>
            <Divider style={{
                marginLeft: '10px',
                marginRight: '10px',
                backgroundColor: 'white'
            }} />
            <List classes={{
                root: style.drawerList

            }}>
                <ListItem button key={'back-to-home'} onClick={switchOpenSate}>
                    <Tooltip title={open ? i18next.t('drawer:close') : i18next.t('drawer:open')}>
                        <ListItemButton
                            sx={[
                                {
                                    minHeight: 48,
                                    px: 2.5,
                                },
                                open
                                    ? {
                                        justifyContent: 'end',
                                    }
                                    : {
                                        justifyContent: 'center',
                                    },
                            ]}>
                            <ListItemIcon
                                className={style.drawerListIcon}
                                sx={[
                                    {
                                        justifyContent: 'center',
                                        minWidth: 0,
                                    },
                                    open
                                        ? {
                                            mr: 0,
                                        }
                                        : {
                                            mr: 'auto',
                                        },
                                ]}>
                                {open ? getIcon('back', false) : getIcon('forward', false)}
                            </ListItemIcon>
                            {/* <ListItemText primary={'Volver al home'} className={style.drawerListItem}/> */}
                        </ListItemButton>
                    </Tooltip>
                </ListItem>
            </List>
        </MuiDrawer>
    );
}

export default Drawer;